/**
 * GNSS integrity alarms (feedback fb-mrwmsuxk-c): poor accuracy, lost fix, and a
 * sudden position jump that could be spoofing. Pure so the thresholds are
 * testable without a live receiver; a runner gathers the inputs from the stores
 * each tick and merges the result into the live alarm state, where the siren,
 * haptics and banner already read from.
 *
 * All three are judged on the REAL device feed only. The simulator (training)
 * drives a clean, continuous position we trust, so when the sim is the source we
 * return everything null and none of these can fire — the same exemption the
 * outlier rejection and the fix-health greying already make for the sim.
 *
 * The detections are deliberately reused rather than recomputed:
 *   - accuracy comes straight off the fix pipeline (ownship.accuracyM),
 *   - lost fix rides the existing fix-health band (already 'lost' past ~30 s),
 *   - the jump reuses the outlier rejection's teleport detection, surfaced as a
 *     timestamp the alarm stands on for a short window.
 */

export interface GnssAlarmInputs {
  /** True only when the real device GPS is the source; the sim is trusted. */
  onDevice: boolean;
  /** Reported horizontal accuracy now, metres, or null when unknown. */
  accuracyM: number | null;
  accuracyEnabled: boolean;
  /** Warn when accuracy is worse (larger) than this, metres. */
  accuracyLimitM: number;
  lostEnabled: boolean;
  /** The fix-health band from the shared runner ('lost' past the timeout). */
  fixHealth: 'live' | 'stale' | 'lost' | null;
  /** How old the last fix is, seconds, for the alarm body. */
  fixAgeSec: number | null;
  jumpEnabled: boolean;
  /** When the ingest last flagged a teleport (ms), or null. */
  lastTeleportMs: number | null;
  /** Now (ms), injected for testability. */
  nowMs: number;
  /** How long the jump alarm stands after the last teleport, ms. */
  jumpWindowMs: number;
}

export interface GnssAlarmLive {
  gnssAccuracy: { accuracyM: number; limitM: number } | null;
  gnssLost: { ageSec: number | null } | null;
  gnssJump: { sinceMs: number } | null;
}

/** The three GNSS integrity alarms, all null unless we're on the device feed. */
export function evaluateGnssAlarms(i: GnssAlarmInputs): GnssAlarmLive {
  const off: GnssAlarmLive = { gnssAccuracy: null, gnssLost: null, gnssJump: null };
  if (!i.onDevice) return off;

  const gnssAccuracy =
    i.accuracyEnabled && i.accuracyM != null && i.accuracyM > i.accuracyLimitM
      ? { accuracyM: i.accuracyM, limitM: i.accuracyLimitM }
      : null;

  const gnssLost = i.lostEnabled && i.fixHealth === 'lost' ? { ageSec: i.fixAgeSec } : null;

  const gnssJump =
    i.jumpEnabled &&
    i.lastTeleportMs != null &&
    i.nowMs - i.lastTeleportMs >= 0 &&
    i.nowMs - i.lastTeleportMs <= i.jumpWindowMs
      ? { sinceMs: i.nowMs - i.lastTeleportMs }
      : null;

  return { gnssAccuracy, gnssLost, gnssJump };
}

/** How long the spoofing/jump alarm stands after the last detected teleport. */
export const GNSS_JUMP_WINDOW_MS = 15_000;
